import {
  VStack,
  Center,
  Text,
  Spacer,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Button,
  Spinner,
} from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { userVerify } from "./SessionApi";
import { ofiEmail } from "../Components/foreningen";
import { KeyIcon } from "lucide-react";
import { log } from "console";

export function SignupVerification() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");

  const [
    passwordVerificationErrorMessage,
    setPasswordVerificationErrorMessage,
  ] = useState<string | null>(null);
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const [responseReceived, setResponseReceived] = useState(false);
  const [verified, setVerified] = useState(false);
  const [password, setPassword] = useState("");

  const triggerVerification = useCallback(
    async (password: string) => {
      if (code) {
        setWaitingForResponse(true);
        userVerify(code, password)
          .then((r) => {
            setVerified(true);
            setResponseReceived(true);
          })
          .catch((e: any) => {
            setVerified(false);
            if (e.response?.status === 401 || e.response?.status === 403) {
              //TODO: sleep 2 seconds
              setPasswordVerificationErrorMessage("Fel lösenord, försök igen!");
            } else {
              console.error("Other error:", e);
              setResponseReceived(true);
            }
          })
          .finally(() => {
            setWaitingForResponse(false);
          });
      }
    },
    [code, setVerified, setResponseReceived, setPassword]
  );

  if (code == null || code == "") {
    return (
      <VStack spacing={"3"}>
        <Text fontSize={"30px"}>Verifieringskod saknas</Text>
      </VStack>
    );
  } else if (!responseReceived) {
    return (
      <VStack spacing={"3"}>
        <Center>
          <Text fontSize={"40px"}>Aktivera din användare</Text>
        </Center>

        <Text fontSize={"20px"} margin={"30px"} textAlign={"left"}>
          Verifiera ditt valda lösenord för att slutföra aktiveringen av din
          användare!
        </Text>

        <HStack>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <KeyIcon color="yellow" opacity={"80%"} />
            </InputLeftElement>
            <Input
              placeholder="Lösenord"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </InputGroup>
          <Button
            rightIcon={waitingForResponse ? <Spinner /> : undefined}
            margin={"40px"}
            minWidth={"100px"}
            colorScheme="blue"
            isDisabled={waitingForResponse || (password?.length ?? 0) < 4}
            onClick={() => {
              if (waitingForResponse || (password?.length ?? 0) >= 4) {
                triggerVerification(password);
              } else {
                console.log("Disabled verify button");
              }
            }}
          >
            Aktivera
          </Button>
        </HStack>

        {passwordVerificationErrorMessage && (
          <Text textColor={"red"} fontWeight={"bold"} textStyle={"italic"}>
            {passwordVerificationErrorMessage}
          </Text>
        )}
      </VStack>
    );
  } else if (!verified) {
    return (
      <VStack>
        <Center>
          <Text fontSize={"40px"}>Länken är inte gilltig längre</Text>
        </Center>
        <Text fontSize={"20px"}>
          Du är välkommen att försöka registrera dig igen. Om felet kvarstår så
          kan du höra av dig till {ofiEmail} om hjälp.
        </Text>
      </VStack>
    );
  }

  return (
    <VStack spacing={"3"} width={"100%"} textAlign={"left"}>
      <Center>
        <Text fontSize={"40px"}>Välkommen!</Text>
      </Center>

      <Text fontSize={"20px"} margin={"30px"} textAlign={"left"}>
        Du är nu en registrerad användare på Östhammar Friidrotts hemsida.
      </Text>

      <Text fontSize={"20px"} textAlign={"center"}>
        Genom att logga in med din email-adress och ditt lösenord (uppe i
        översta högra hörnet) kan du nu bland annat se och hantera dina
        anmälningar på Östhammar Friidrotts listade tävlingar och event.
      </Text>
      <Text fontStyle={"italic"} marginTop={"200px"} color={"gray.400"}>
        Din roll är från start satt till "USER" vilket är den lägsta rollen.
        Vänligen kontakta Jonathan Ruffieux om du även bör ha administrativa
        rättigheter.
      </Text>
    </VStack>
  );
}
